import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SectionTitle from "@/components/SectionTitle";

const FAQ = [
  ["Is IOCRI a government agency?", "No — IOCRI is an independent, non-governmental research institute."],
  ["How do I cite IOCRI research?", "Use APA 7. All papers include a formal citation string on the cover page."],
  ["Can I submit a story lead?", "Yes — use this form or email press@iocri.example."],
  ["Do you offer speaking engagements?", "Our fellows accept a limited number of invitations per year. Please reach out via this form."],
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/contact", form); toast.success("Message sent."); setForm({ name: "", email: "", subject: "", message: "" }); }
    catch { toast.error("Failed to send message."); }
    finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="contact-page">
      <SectionTitle eyebrow="CONTACT" title="Get in touch with IOCRI." subtitle="For research inquiries, media, partnerships or support requests." />

      <div className="mt-10 grid lg:grid-cols-[1.4fr,1fr] gap-6">
        <Card className="bg-obsidian-soft border-white/5 rounded-2xl">
          <CardContent className="p-8">
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="contact-form">
              <Input required placeholder="Full name" value={form.name} onChange={upd("name")} data-testid="contact-name" className="bg-black border-white/10 rounded-xl h-11 text-white" />
              <Input required type="email" placeholder="Email" value={form.email} onChange={upd("email")} data-testid="contact-email" className="bg-black border-white/10 rounded-xl h-11 text-white" />
              <Input required placeholder="Subject" value={form.subject} onChange={upd("subject")} data-testid="contact-subject" className="md:col-span-2 bg-black border-white/10 rounded-xl h-11 text-white" />
              <Textarea required rows={6} placeholder="Your message" value={form.message} onChange={upd("message")} data-testid="contact-message" className="md:col-span-2 bg-black border-white/10 rounded-xl text-white" />
              <Button type="submit" disabled={loading} data-testid="contact-submit" className="md:col-span-2 rounded-full bg-gold text-black hover:bg-gold-light h-11">
                {loading ? "Sending…" : <><Send className="h-4 w-4 mr-2" /> Send message</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {[[Mail, "press@iocri.example"], [Phone, "+1 (000) 000-0000"], [MapPin, "Global · Remote-first"]].map(([I, t], i) => (
            <div key={i} className="rounded-2xl border border-white/5 bg-obsidian-soft p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center"><I className="h-5 w-5 text-gold" /></div>
              <div className="font-display text-lg text-white">{t}</div>
            </div>
          ))}
          <div className="rounded-2xl border border-white/5 bg-obsidian-soft h-56 grid place-items-center text-white/40 font-mono text-xs tracking-widest" data-testid="contact-map">
            MAP PLACEHOLDER
          </div>
        </div>
      </div>

      <div className="mt-16">
        <div className="text-gold font-mono text-xs tracking-widest">FREQUENTLY ASKED QUESTIONS</div>
        <div className="mt-4 divide-y divide-white/5 rounded-2xl border border-white/5 bg-obsidian-soft">
          {FAQ.map(([q, a], i) => (
            <details key={i} className="group p-6" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer flex items-center justify-between font-display text-lg text-white">{q}<span className="text-gold ml-4">＋</span></summary>
              <div className="mt-3 text-white/70 text-sm">{a}</div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}
